import { useEffect, useState } from "react";
import { useActivityApi } from "../../hooks/useActivityApi";
import { ActivityDTO, MemberDTO } from "../../services/apiClient";
import { getActivityCountByType, getComplianceActivityTypes, getComplianceTitle, isDedicatedCategory } from "../../utils/complianceUtils";
import { MemberTypeConstants } from "../../constants/MemberTypeConstants";

interface ComplianceOverviewProps {
    member: MemberDTO | null;
}

const ComplianceOverview: React.FC<ComplianceOverviewProps> = ({ member }) => {

    const { getComplianceActivities, isLoading, error } = useActivityApi();
    const [items, setItems] = useState<ActivityDTO[]>([]);
    const [requiredActivities, setRequiredActivities] = useState<any[]>([]);
    const [complianceTitle, setComplianceTitle] = useState<string | null>(null);

    useEffect(() => {
        const fetchComplianceActivities = async () => {
            try {
                const response = await getComplianceActivities();
                setItems(response?.data || []);
            } catch (error) {
                console.error('Error fetching Compliance Activities', error);
            }
        };

        fetchComplianceActivities();
    }, []);

    useEffect(() => {
        if (member) {
            setRequiredActivities(getComplianceActivityTypes(member!.memberCategoryId!, member));
            setComplianceTitle(getComplianceTitle(member!.dateOfBirth))
        }

    }, [items, member]);

    function StatBox({ label, value, color }: { label: string; value: string; color: string }) {
        return (
            <div className="rounded-lg bg-gray-50 p-4 text-center">
                <div className={`text-xl font-bold ${color}`}>{value}</div>
                <div className={`text-xs text-gray-600 ${color}`}>{label}</div>
            </div>
        );
    }

    return (
        <>
            {!isLoading && member && (
                <>
                    {!isDedicatedCategory(member!.memberCategoryId!)
                        ? (null)
                        : (
                            <div className="space-y-6">
                                {/* Status grid */}
                                <div className="rounded-2xl bg-white p-4 shadow">
                                    <div className="mb-3 flex items-center justify-between">
                                        <h3 className="text-sm font-semibold">{complianceTitle}</h3>
                                        {(() => {
                                            const allCompleted = requiredActivities.every(activity => {
                                                // If activity is not needed, consider it as completed
                                                if (!activity.isNeeded) return true;

                                                const currentCount = getActivityCountByType(items, activity.activityTypeId);
                                                return currentCount >= activity.requiredCount;
                                            });
                                            return (
                                                <span className={`rounded-full border px-2 py-0.5 text-xs ${allCompleted ? 'text-green-600 border-green-600' : 'text-orange-600 border-orange-600'}`}>
                                                    {allCompleted ? 'Completed' : 'Not Completed'}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="grid grid-cols-2 gap-1 flex-1">
                                            {requiredActivities.map((activity, index) => {
                                                // If activity is not needed, show "Not Needed"
                                                if (!activity.isNeeded) {
                                                    return (
                                                        <StatBox
                                                            key={index}
                                                            label={activity.type.toUpperCase()}
                                                            value="Not Needed"
                                                            color="text-green-600"
                                                        />
                                                    );
                                                }

                                                const currentCount = getActivityCountByType(items, activity.activityTypeId);
                                                return (
                                                    <StatBox
                                                        key={index}
                                                        label={activity.type.toUpperCase()}
                                                        value={`${currentCount} / ${activity.requiredCount}`}
                                                        color={currentCount >= activity.requiredCount ? "text-green-600" : "text-orange-600"}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <div className="flex-shrink-0">
                                            {(() => {
                                                // Check if member is a life member
                                                const isLifeMember = member?.memberTypeId?.toLowerCase() === MemberTypeConstants.LIFE_MEMBER.toLowerCase() ||
                                                    member?.memberTypeId?.toLowerCase() === MemberTypeConstants.DEDICATED_LIFE_MEMBER.toLowerCase();

                                                let paidUpValue = 'NO';
                                                let paidUpColor = 'text-orange-600';

                                                if (isLifeMember) {
                                                    // Life members are always paid up
                                                    paidUpValue = 'YES';
                                                    paidUpColor = 'text-green-600';
                                                } else if (member?.paidUpTo) {
                                                    const paidUpDate = new Date(member.paidUpTo);
                                                    if (paidUpDate > new Date()) {
                                                        paidUpValue = 'YES';
                                                        paidUpColor = 'text-green-600';
                                                    }
                                                }
                                                return <StatBox label="PAID-UP" value={paidUpValue} color={paidUpColor} />;
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                </>
            )}
        </>
    );
}

export default ComplianceOverview;